import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// ============= TOMMY'S TIME-OF-DAY SCENE MASHUPS =============
// Morning (5am-12pm): Belly exposed in butterfly meadow - cozy & fresh
// Afternoon (12pm-6pm): Pouncing at hermit crab on beach - energetic!
// Evening (6pm-10pm): Cozy belly exposed (transition to sleepy)
// Night (10pm-5am): Rubbing eyes with yarn ball nearby - sleepy & cozy

const TIME_OF_DAY_SCENES = {
  morning: {
    image: 'https://static.prod-images.emergentagent.com/jobs/5a9b2831-6b5b-4cfc-b7a9-9566f26820d4/images/02f9478e4053916af73bc6e2bcae6f5692e7e4579ab94fac6ff9fde46930d57b.png',
    alt: 'Tommy belly-up in butterfly meadow',
    mood: 'cozy',
    greeting: 'Good morning! ☀️',
    particleColor: 'rgba(255, 200, 100, 0.5)' // Warm morning gold
  },
  afternoon: {
    image: 'https://static.prod-images.emergentagent.com/jobs/5a9b2831-6b5b-4cfc-b7a9-9566f26820d4/images/3aaff48c3c108403ad40659fae36d583584e6452fcfa7558f91d80b40a192b08.png',
    alt: 'Tommy pouncing at hermit crab on beach',
    mood: 'playful',
    greeting: 'Let\'s get stuff done! 🏖️',
    particleColor: 'rgba(20, 184, 166, 0.5)' // Energetic teal
  },
  evening: {
    image: 'https://static.prod-images.emergentagent.com/jobs/5a9b2831-6b5b-4cfc-b7a9-9566f26820d4/images/e91a380c1ad0a4127c2f65b8baa1d0955cd1529bf0d749329e419e6a384c5397.png',
    alt: 'Tommy showing cozy belly',
    mood: 'cozy',
    greeting: 'Time to wind down 🌅',
    particleColor: 'rgba(255, 150, 80, 0.4)' // Warm sunset orange
  },
  night: {
    image: 'https://static.prod-images.emergentagent.com/jobs/5a9b2831-6b5b-4cfc-b7a9-9566f26820d4/images/3d55ecd59877726bd28a38c496ffe46eeca02f200ced7ce4fb20b0f2bbcdc185.png',
    alt: 'Tommy rubbing eyes sleepily with yarn ball',
    mood: 'sleepy',
    greeting: 'Working late? 🌙',
    particleColor: 'rgba(150, 130, 200, 0.3)' // Soft purple night
  }
};

// ============= ANIMATIONS PER MOOD =============
const MOOD_ANIMATIONS = {
  sleepy: {
    play: {
      scale: [1, 1.01, 1, 1.005, 1],
      rotate: [0, 0.5, -0.3, 0.2, 0],
      y: [0, -2, 0, -1, 0],
    },
    transition: { duration: 4, ease: "easeInOut" }
  },
  playful: {
    play: {
      scale: [1, 1.04, 0.98, 1.03, 1],
      x: [0, 8, -5, 4, 0],
      y: [0, -10, -3, -8, 0],
      rotate: [0, 2, -2, 1, 0],
    },
    transition: { duration: 2.5, ease: "easeOut" }
  },
  cozy: {
    play: {
      scale: [1, 1.015, 1.01, 1.02, 1],
      rotate: [0, 1, -0.5, 0.5, 0],
      y: [0, -1, 1, 0, 0],
    },
    transition: { duration: 3.5, ease: "easeInOut" }
  }
};

// Get time of day
function getTimeOfDay() {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 18) return 'afternoon';
  if (hour >= 18 && hour < 22) return 'evening';
  return 'night';
}

// Soothing audio chimes per mood
function createChimeSound(audioContext, mood) {
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();
  
  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);
  
  const now = audioContext.currentTime;
  
  switch (mood) {
    case 'sleepy':
      // Soft, low, calming - like a gentle lullaby
      oscillator.frequency.setValueAtTime(392, now); // G4
      oscillator.frequency.exponentialRampToValueAtTime(440, now + 0.15); // A4
      oscillator.frequency.exponentialRampToValueAtTime(392, now + 0.3); // G4
      gainNode.gain.setValueAtTime(0, now);
      gainNode.gain.linearRampToValueAtTime(0.04, now + 0.05);
      gainNode.gain.exponentialRampToValueAtTime(0.01, now + 1.2);
      oscillator.stop(now + 1.2);
      break;
      
    case 'playful':
      // Bright, energetic, ascending - beach vibes!
      oscillator.frequency.setValueAtTime(523.25, now); // C5
      oscillator.frequency.exponentialRampToValueAtTime(659.25, now + 0.08); // E5
      oscillator.frequency.exponentialRampToValueAtTime(783.99, now + 0.16); // G5
      oscillator.frequency.exponentialRampToValueAtTime(1046.5, now + 0.24); // C6
      gainNode.gain.setValueAtTime(0, now);
      gainNode.gain.linearRampToValueAtTime(0.07, now + 0.03);
      gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.5);
      oscillator.stop(now + 0.5);
      break;
      
    case 'cozy':
      // Warm, descending, relaxing - meadow & sunset vibes
      oscillator.frequency.setValueAtTime(659.25, now); // E5
      oscillator.frequency.exponentialRampToValueAtTime(523.25, now + 0.2); // C5
      oscillator.frequency.exponentialRampToValueAtTime(440, now + 0.4); // A4
      gainNode.gain.setValueAtTime(0, now);
      gainNode.gain.linearRampToValueAtTime(0.05, now + 0.05);
      gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.9);
      oscillator.stop(now + 0.9);
      break;
      
    default:
      oscillator.frequency.setValueAtTime(523.25, now);
      oscillator.frequency.exponentialRampToValueAtTime(659.25, now + 0.1);
      gainNode.gain.setValueAtTime(0, now);
      gainNode.gain.linearRampToValueAtTime(0.05, now + 0.05);
      gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.7);
      oscillator.stop(now + 0.7);
  }
  
  oscillator.type = 'sine';
  oscillator.start(now);
}

export function TommyBackground({ opacity = 0.15 }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [timeOfDay, setTimeOfDay] = useState(getTimeOfDay());
  const audioContextRef = useRef(null);
  const intervalRef = useRef(null);
  const timeCheckRef = useRef(null);

  // Get current scene data
  const sceneData = useMemo(() => {
    return TIME_OF_DAY_SCENES[timeOfDay];
  }, [timeOfDay]);

  const animation = useMemo(() => {
    return MOOD_ANIMATIONS[sceneData.mood];
  }, [sceneData.mood]);

  // Initialize audio context
  const initAudio = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    return audioContextRef.current;
  }, []);

  // Play soothing chime
  const playChime = useCallback(() => {
    try {
      const ctx = initAudio();
      if (ctx.state === 'suspended') {
        ctx.resume();
      }
      createChimeSound(ctx, sceneData.mood);
    } catch (e) {
      // Silently fail if audio not available
    }
  }, [initAudio, sceneData.mood]);

  // Check and update time of day periodically
  useEffect(() => {
    const checkTime = () => {
      const newTimeOfDay = getTimeOfDay();
      if (newTimeOfDay !== timeOfDay) {
        setTimeOfDay(newTimeOfDay);
      }
    };
    
    // Check every minute
    timeCheckRef.current = setInterval(checkTime, 60000);
    
    return () => {
      if (timeCheckRef.current) {
        clearInterval(timeCheckRef.current);
      }
    };
  }, [timeOfDay]);

  // Animation cycle
  useEffect(() => {
    const playDuration = sceneData.mood === 'playful' ? 2500 : sceneData.mood === 'sleepy' ? 4000 : 3500;
    const pauseDuration = sceneData.mood === 'playful' ? 4000 : 5000;
    const cycleDuration = playDuration + pauseDuration;
    
    const cycle = () => {
      setIsPlaying(true);
      
      setTimeout(() => {
        setIsPlaying(false);
        playChime();
      }, playDuration);
    };

    // Initial delay
    const initialDelay = setTimeout(() => {
      cycle();
      intervalRef.current = setInterval(cycle, cycleDuration);
    }, 1500);

    return () => {
      clearTimeout(initialDelay);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [playChime, sceneData.mood]);

  // Check for reduced motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setIsVisible(!mediaQuery.matches);
    
    const handler = (e) => setIsVisible(!e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  if (!isVisible) return null;

  return (
    <div 
      className="fixed inset-0 pointer-events-none overflow-hidden z-0"
      aria-hidden="true"
      data-testid="tommy-background"
      data-time-of-day={timeOfDay}
      data-mood={sceneData.mood}
    >
      {/* Ambient gradient based on time of day */}
      <div 
        className="absolute inset-0 transition-all duration-1000"
        style={{
          background: timeOfDay === 'morning' 
            ? 'radial-gradient(ellipse at center, transparent 20%, rgba(255,240,200,0.1) 50%, rgba(0,0,0,0.7) 100%)'
            : timeOfDay === 'afternoon'
            ? 'radial-gradient(ellipse at center, transparent 20%, rgba(100,200,255,0.08) 50%, rgba(0,0,0,0.7) 100%)'
            : timeOfDay === 'evening'
            ? 'radial-gradient(ellipse at center, transparent 20%, rgba(255,150,100,0.1) 50%, rgba(0,0,0,0.7) 100%)'
            : 'radial-gradient(ellipse at center, transparent 20%, rgba(100,100,150,0.1) 50%, rgba(0,0,0,0.8) 100%)'
        }}
      />
      
      {/* Tommy image with animation */}
      <motion.div
        className="absolute inset-0 flex items-center justify-center"
        style={{ opacity }}
      >
        <AnimatePresence mode="wait">
          <motion.img
            key={sceneData.image}
            src={sceneData.image}
            alt={sceneData.alt}
            className="w-full h-full object-cover object-center"
            style={{
              filter: 'blur(1px) saturate(0.9)',
              transform: 'scale(1.1)'
            }}
            initial={{ opacity: 0 }}
            animate={isPlaying ? 
              { ...animation.play, opacity: 1 } : 
              { scale: 1, x: 0, y: 0, rotate: 0, opacity: 1 }
            }
            exit={{ opacity: 0 }}
            transition={isPlaying ? animation.transition : { duration: 0.5, ease: "easeOut" }}
          />
        </AnimatePresence>
      </motion.div>

      {/* Vignette for content focus */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 30%, rgba(9, 9, 11, 0.6) 100%)',
          pointerEvents: 'none'
        }}
      />

      {/* Mood-based particles */}
      <AnimatePresence>
        {isPlaying && (
          <>
            {[...Array(sceneData.mood === 'playful' ? 10 : 5)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute rounded-full"
                style={{
                  width: sceneData.mood === 'playful' ? '6px' : '4px',
                  height: sceneData.mood === 'playful' ? '6px' : '4px',
                  background: sceneData.particleColor,
                  left: `${15 + Math.random() * 70}%`,
                  top: `${25 + Math.random() * 50}%`,
                }}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ 
                  opacity: [0, 0.8, 0],
                  scale: [0, 1.3, 0.5],
                  y: sceneData.mood === 'playful' ? [0, -35, -50] : [0, -15, -25]
                }}
                exit={{ opacity: 0 }}
                transition={{ 
                  duration: sceneData.mood === 'playful' ? 1.8 : 2.5,
                  delay: i * 0.12,
                  ease: "easeOut"
                }}
              />
            ))}
          </>
        )}
      </AnimatePresence>

      {/* Time of day greeting (subtle, bottom right) */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.4 }}
        className="absolute bottom-4 right-4 text-xs text-white/60 font-medium tracking-wide"
      >
        {sceneData.greeting}
      </motion.div>
    </div>
  );
}

export default TommyBackground;
